'use client';

import React, { useEffect, useState } from 'react';
import { Footer } from '@/components/public/footer';
import { NavPreview } from '@/components/public/nav-preview';
import { useScrollReveal } from '@/lib/hooks/use-scroll-reveal';
import Image from 'next/image';
import { Link } from '@/i18n/navigation';
import { useTranslations, useLocale } from 'next-intl';
import { FaqAccordion } from '@/components/public/faq-accordion';
import { FAQ_CATEGORIES } from '@/lib/faq-content';
import { useFaqItems } from '@/lib/faq-hooks';
import { X, Check, Loader2 } from 'lucide-react';

function IndividualInquiryModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useTranslations('design.newYearsEve2027');
  const locale = useLocale();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [roomType, setRoomType] = useState('');
  const [rooms, setRooms] = useState('');
  const [details, setDetails] = useState('');

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => setSubmitted(false), 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Dedicated individual endpoint — NOT shared with the group form above.
      const res = await fetch('/api/inquiries/new-years-eve-individual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName,
          phone: phoneNumber,
          email,
          roomType,
          rooms,
          details,
          locale,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || 'send_failed');
      }
      setSubmitted(true);
    } catch {
      setSubmitError(t('preRegisterForm.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const inputCls = "w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#faf8f6] focus:border-[#801515] focus:ring-2 focus:ring-[#801515]/10 outline-none transition-all duration-300 text-gray-900";
  const labelCls = "block text-sm font-medium text-gray-700 mb-1.5";

  return (
    <div
      className="fixed inset-0 z-[90] flex items-start justify-center overflow-y-auto bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl mx-4 my-8 md:my-16 bg-white rounded-3xl shadow-2xl overflow-hidden animate-[slideInUp_0.5s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/20 hover:bg-black/40 backdrop-blur-sm flex items-center justify-center text-white transition-colors duration-300"
        >
          <X className="w-5 h-5" />
        </button>

        {submitted ? (
          /* ── Success state ── */
          <div className="px-8 md:px-16 py-16 text-center">
            <div className="w-20 h-20 rounded-full bg-[#4A6741]/10 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-[#4A6741]" />
            </div>
            <h2 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('preRegisterForm.thankYou')}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-2">
              {t('preRegisterForm.requestReceived')}
            </p>
            <p className="text-gray-500 leading-relaxed mb-10">
              {t('preRegisterForm.replyMessage')}
            </p>
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 bg-[#801515] hover:bg-[#601010] text-white px-8 py-4 rounded-full font-medium transition-all duration-300 hover:shadow-xl"
            >
              {t('preRegisterForm.close')}
            </button>
          </div>
        ) : (
          /* ── Form ── */
          <form onSubmit={handleSubmit} className="px-8 md:px-16 py-10">
            <div className="text-center mb-10">
              <h2 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                {t('preRegisterForm.title')}
              </h2>
              <p className="text-gray-500 leading-relaxed max-w-lg mx-auto">
                {t('preRegisterForm.subtitle')}
              </p>
            </div>

            <div className="space-y-6">
              {/* Full Name */}
              <div>
                <label className={labelCls}>{t('preRegisterForm.fullName')} <span className="text-[#801515]">*</span></label>
                <input
                  type="text"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className={inputCls}
                  placeholder={t('preRegisterForm.fullNamePlaceholder')}
                />
              </div>

              {/* Phone Number */}
              <div>
                <label className={labelCls}>{t('preRegisterForm.phoneNumber')} <span className="text-[#801515]">*</span></label>
                <input
                  type="tel"
                  required
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className={inputCls}
                  placeholder="+40 ..."
                />
              </div>

              {/* Email Address */}
              <div>
                <label className={labelCls}>{t('preRegisterForm.email')} <span className="text-[#801515]">*</span></label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputCls}
                  placeholder="your@email.com"
                />
              </div>

              {/* Preferred Room Type */}
              <div>
                <label className={labelCls}>{t('preRegisterForm.roomType')} <span className="text-gray-400">({t('preRegisterForm.optional')})</span></label>
                <input
                  type="text"
                  value={roomType}
                  onChange={(e) => setRoomType(e.target.value)}
                  className={inputCls}
                  placeholder={t('preRegisterForm.roomTypePlaceholder')}
                />
              </div>

              {/* Preferred Rooms */}
              <div>
                <label className={labelCls}>{t('preRegisterForm.rooms')} <span className="text-gray-400">({t('preRegisterForm.optional')})</span></label>
                <input
                  type="text"
                  value={rooms}
                  onChange={(e) => setRooms(e.target.value)}
                  className={inputCls}
                  placeholder={t('preRegisterForm.roomsPlaceholder')}
                />
              </div>

              {/* Details */}
              <div>
                <label className={labelCls}>{t('preRegisterForm.details')} <span className="text-gray-400">({t('preRegisterForm.optional')})</span></label>
                <textarea
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  rows={4}
                  className={`${inputCls} resize-none`}
                  placeholder={t('preRegisterForm.detailsPlaceholder')}
                />
              </div>
            </div>

            {/* ── Submit ── */}
            <div className="text-center pt-8 pb-4">
              {submitError && (
                <div className="mb-4 mx-auto max-w-md px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                  {submitError}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting || !email || !fullName || !phoneNumber}
                className="inline-flex items-center gap-3 bg-[#801515] hover:bg-[#601010] disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-10 py-4 rounded-full font-medium text-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('preRegisterForm.sending')}
                  </>
                ) : (
                  <>{t('preRegisterForm.submitButton')}</>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function FaqSection() {
  const category = FAQ_CATEGORIES.find(c => c.id === 'newYearsEve2027')!;
  const items = useFaqItems(category);
  const t = useTranslations('design.newYearsEve2027');

  return (
    <section className="relative py-24 lg:py-32 bg-gradient-to-b from-white to-[#faf8f6]">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16 observe-me slide-up">
          <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6">
            <span className="text-[#801515]">{t('faq.heading')}</span>
            <br />
            <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
              {t('faq.subheading')}
            </span>
          </h2>
          <div className="w-16 h-1 bg-[#801515] mx-auto" />
        </div>
        <div className="observe-me slide-up">
          <FaqAccordion items={items} accent={category.accent} />
        </div>
      </div>
    </section>
  );
}

export default function NewYearsEve2027OfferPage() {
  const t = useTranslations('design.newYearsEve2027');
  const [individualInquiryOpen, setIndividualInquiryOpen] = useState(false);

  useScrollReveal();

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes breathe {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.08); }
      }
      @keyframes breatheSlow {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }
      @keyframes slideInUp {
        from { opacity: 0; transform: translateY(60px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes pulse {
        0%, 100% { opacity: 0.3; }
        50% { opacity: 0.6; }
      }
      @keyframes bubblePulse {
        0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(128, 21, 21, 0.4); }
        50% { transform: scale(1.05); box-shadow: 0 0 0 8px rgba(128, 21, 21, 0); }
      }
      .image-breathe {
        animation: breathe 8s ease-in-out infinite;
      }
      .image-breathe-slow {
        animation: breatheSlow 10s ease-in-out infinite;
      }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  return (
    <main className="min-h-screen bg-white text-gray-900">
      <NavPreview />

      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          <Image
            src="/design-images/uploads/1786363249006-xpgcj3-cd-5-.jpg"
            alt={t('hero.imageAlt')}
            fill
            className="object-cover object-center image-breathe-slow"
            priority
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />
        <div className="relative h-full flex flex-col items-center justify-center px-6 pt-56 lg:pt-16 text-center">
          <div className="observe-me slide-up w-full max-w-3xl space-y-6">
            <h1 className="font-poppins text-3xl sm:text-5xl md:text-7xl font-bold text-white tracking-tight break-words whitespace-pre-line" style={{ textShadow: '2px 2px 30px rgba(0, 0, 0, 0.5)' }}>
              {t('hero.title')}
            </h1>
            <p className="font-poppins text-base sm:text-lg md:text-2xl text-white tracking-wide" style={{ textShadow: '1px 1px 20px rgba(0, 0, 0, 0.5)' }}>
              {t('hero.subtitle')}
            </p>

            {/* Offer CTAs */}
            <div className="flex flex-col sm:flex-row justify-center gap-8 pt-8">
              <div className="flex flex-col items-center gap-4">
                <Link
                  href="#individual-offer"
                  className="group flex flex-col items-center justify-center gap-0.5 px-6 py-3 bg-[#801515] hover:bg-[#601010] text-white font-serif text-sm sm:text-base rounded-full shadow-lg hover:shadow-xl transition-all duration-300 text-center leading-tight"
                  style={{ animation: 'bubblePulse 2s ease-in-out infinite' }}
                >
                  <span>{t('ctas.individualLine1')}</span>
                  <span>{t('ctas.individualLine2')}</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="grid lg:grid-cols-5 min-h-[90vh] design-section-gap rounded-3xl overflow-hidden">
        {/* Image Column */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-2 lg:order-1 observe-me slide-left">
          <Image
            src="/design-images/uploads/1786438060265-035fho-chatgpt-image-11-aug.-2026-09_03_51.jpg"
            alt={t('welcome.p3')}
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
          />
        </div>

        {/* Text Column */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#faf8f6] to-white order-1 lg:order-2">
          <div className="max-w-lg observe-me slide-right">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('welcome.p3')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('welcome.subtitle')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-6">
              {t('welcome.p1')}
            </p>
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-8">
              {t('welcome.p2')}
            </p>

            {/* Details */}
            <div className="space-y-2 mb-8">
              {[
                { label: t('details.duration'), text: '' },
                { label: t('details.checkIn'), text: '' },
                { label: t('details.checkOut'), text: '', spaceBefore: true },
              ].map((detail, i) => (
                <div key={i}>
                  {detail.spaceBefore && <div className="h-2" />}
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-[#801515] rounded-full mt-3" />
                    <p className="text-gray-600">
                      <strong className="text-gray-900">{detail.label}</strong>
                    </p>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* Guest Experience Section */}
      <section className="grid lg:grid-cols-5 min-h-[90vh] design-section-gap rounded-3xl overflow-hidden">
        {/* Text Column */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-white order-1">
          <div className="max-w-lg observe-me slide-left">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('guestExperience.title')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('guestExperience.subtitle')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-8">
              {t('guestExperience.description')}
            </p>

            {/* CTA */}
            <a
              href="https://inspiratielafix.com/experienta-act-for-good-magie-intre-ani-la-castle-hotel-daniel/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-[#801515] hover:bg-[#601010] text-white font-serif text-sm sm:text-base rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              {t('guestExperience.cta')}
            </a>
          </div>
        </div>

        {/* Image Column */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-2 observe-me slide-right">
          <Image
            src="/design-images/uploads/1786461752894-1jdlwl-whatsapp-image-2026-08-10-at-18.09.48-3-.jpg"
            alt={t('guestExperience.imageAlt')}
            fill
            className="object-cover object-top image-breathe hover:scale-110 transition-transform duration-700"
          />
        </div>
      </section>

{/* Individual Offer Section - Main Hero */}
      <section id="individual-offer" className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-2 lg:order-1 observe-me slide-left">
          <Image
            src="/design-images/uploads/1786385656324-io89t0-ds2_3512.jpg"
            alt={t('individualOffer.title')}
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
          />
        </div>

        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-white order-1 lg:order-2">
          <div className="max-w-lg observe-me slide-right">
            <div className="space-y-6">
              <div className="space-y-2 mb-6">
                <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
                  <span className="text-[#801515]">{t('individualOffer.title')}</span>
                  <br />
                  <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                    {t('individualOffer.subtitle')}
                  </span>
                </h2>
              </div>

              <div className="w-16 h-1 bg-[#801515] mb-6" />

              <p className="text-lg xl:text-xl text-gray-600 leading-relaxed">
                {t('individualOffer.description')}
              </p>

              <p className="text-lg xl:text-xl text-gray-700 leading-relaxed">
                {t('individualOffer.intro')}
              </p>

              <p className="text-lg xl:text-xl text-gray-700 leading-relaxed">
                {t('individualOffer.roomSelectionPrompt')}
              </p>

              {/* CTA - After intro paragraph */}
              <div className="pt-6">
                <Link
                  href="/rooms?checkIn=2026-12-30&checkOut=2027-01-03#rooms-list"
                  className="inline-flex items-center justify-center px-6 py-3 bg-[#801515] hover:bg-[#601010] text-white font-serif text-sm sm:text-base rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {t('individualOffer.cta')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Individual Offer Details - Separate Section */}
      <section className="design-section-gap">
        <div className="max-w-5xl mx-auto design-content-pad lg:py-20">
          <div className="observe-me slide-up space-y-12">
            {/* Your Individual Package Includes */}
            <div className="space-y-6">
              <h3 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold text-gray-900">
                {t('individualOffer.included')}
              </h3>

              {/* Included Items List */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Left Column - Accommodation & Activities */}
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5].map((num) => {
                    const itemText = t(`individualOffer.item${num}`);

                    return (
                      <div key={num} className="bg-white rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-[#801515] observe-me slide-up" style={{ animationDelay: `${num * 0.1}s` }}>
                        <p className="text-lg text-gray-900">{itemText}</p>
                      </div>
                    );
                  })}
                </div>

                {/* Right Column - Meal Plan */}
                <div>
                  <div className="bg-[#801515] text-white rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 observe-me slide-up">
                    <p className="font-semibold text-lg">{t('individualOffer.mealplanHeader')}</p>
                  </div>
                  <div className="space-y-4 mt-4">
                    {[1, 2, 3, 4].map((num) => {
                      const itemText = t(`individualOffer.mealplan${num}`);
                      return (
                        <div key={`mealplan${num}`} className="bg-white rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-[#801515] observe-me slide-up" style={{ animationDelay: `${(num + 0.5) * 0.1}s` }}>
                          <p className="text-lg text-gray-900">{itemText}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Pricing Table */}
            <div className="pt-8 space-y-4">
              <h4 className="font-poppins text-2xl font-semibold text-gray-900">
                {t('individualOffer.pricing')}
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-[#801515]">
                      <th className="text-left py-3 px-4 text-lg font-semibold text-gray-900">
                        {t('individualOffer.roomCategory')}
                      </th>
                      <th className="text-right py-3 px-4 text-lg font-semibold text-gray-900">
                        {t('individualOffer.nightPackage')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-4 text-lg text-gray-700">
                        {t('individualOffer.standardRoom')}
                      </td>
                      <td className="text-right py-3 px-4 text-lg font-semibold text-[#801515]">
                        {t('individualOffer.standardPrice')}
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-4 text-lg text-gray-700">
                        {t('individualOffer.superiorRoom')}
                      </td>
                      <td className="text-right py-3 px-4 text-lg font-semibold text-[#801515]">
                        {t('individualOffer.superiorPrice')}
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-4 text-lg text-gray-700">
                        {t('individualOffer.deluxeRoom')}
                      </td>
                      <td className="text-right py-3 px-4 text-lg font-semibold text-[#801515]">
                        {t('individualOffer.deluxePrice')}
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-4 text-lg text-gray-700">
                        {t('individualOffer.signatureRoom')}
                      </td>
                      <td className="text-right py-3 px-4 text-lg font-semibold text-[#801515]">
                        {t('individualOffer.signaturePrice')}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-lg text-gray-700">
                        {t('individualOffer.swanSuite')}
                      </td>
                      <td className="text-right py-3 px-4 text-lg font-semibold text-[#801515]">
                        {t('individualOffer.swanPrice')}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                {t('individualOffer.ratesDisclaimer')}
              </p>
            </div>

            {/* Additional Guests */}
            <div className="space-y-4">
              <h4 className="font-poppins text-2xl font-semibold text-gray-900">
                {t('individualOffer.additionalGuestsHeading')}
              </h4>
              <ul className="space-y-3">
                {[1, 2, 3, 4].map((num) => (
                  <li key={num} className="text-lg xl:text-xl text-gray-700 leading-relaxed flex gap-3">
                    <span className="text-[#801515] font-semibold">•</span>
                    <span>{t(`individualOffer.additionalGuest${num}`)}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Payment and Cancellation Conditions */}
            <div className="space-y-4 pt-4">
              <h4 className="font-poppins text-2xl font-semibold text-gray-900">
                {t('individualOffer.paymentCancellationHeading')}
              </h4>
              <p className="text-lg xl:text-xl text-gray-700 leading-relaxed">
                {t('individualOffer.paymentCancellationText')}
              </p>
            </div>

            {/* Pre-register CTA */}
            <div className="pt-4">
              <Link
                href="/rooms?checkIn=2026-12-30&checkOut=2027-01-03#rooms-list"
                className="inline-flex items-center justify-center px-6 py-3 bg-[#801515] hover:bg-[#601010] text-white font-serif text-sm sm:text-base rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {t('individualOffer.cta')}
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* What Awaits You Section */}
      <section className="design-section-gap">
        <div className="text-center mb-10 pt-16 observe-me slide-up">
          <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-4">
            <span className="text-[#801515]">{t('awaits.heading')}</span>
          </h2>
          <div className="w-16 h-1 bg-[#801515] mx-auto mt-6 mb-4" />
        </div>

        <div className="max-w-5xl mx-auto design-content-pad lg:py-20">
          <div className="observe-me slide-up space-y-12">

            {/* Subsections Grid */}
            <div className="space-y-12 md:space-y-16">
              {/* Stay in a Room with a Story - with Image */}
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="order-2 md:order-1">
                  <h3 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {t('awaits.rooms.title')}
                  </h3>
                  <p className="text-lg xl:text-xl text-gray-700 leading-relaxed">
                    {t('awaits.rooms.body')}
                  </p>
                </div>
                <div className="order-1 md:order-2 rounded-2xl overflow-hidden shadow-lg">
                  <Image
                    src="/design-images/uploads/1786368027025-79hk3g-8_dc_constantinople_1.jpg"
                    alt={t('awaits.rooms.title')}
                    width={500}
                    height={400}
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>

              {/* Slow Down in the Cellarium - with Image */}
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="rounded-2xl overflow-hidden shadow-lg">
                  <Image
                    src="/design-images/uploads/1786368129702-1ggy9k-img_0145.jpg"
                    alt={t('awaits.spa.title')}
                    width={500}
                    height={400}
                    className="w-full h-auto object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {t('awaits.spa.title')}
                  </h3>
                  <p className="text-lg xl:text-xl text-gray-700 leading-relaxed">
                    {t('awaits.spa.body')}
                  </p>
                </div>
              </div>

              {/* Discover Transylvanian Winter Flavours - with Image */}
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="order-2 md:order-1">
                  <h3 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {t('awaits.dining.title')}
                  </h3>
                  <p className="text-lg xl:text-xl text-gray-700 leading-relaxed">
                    {t('awaits.dining.body')}
                  </p>
                </div>
                <div className="order-1 md:order-2 rounded-2xl overflow-hidden shadow-lg">
                  <Image
                    src="/design-images/uploads/1786368188752-zneoqw-ray04706.jpg"
                    alt={t('awaits.dining.title')}
                    width={500}
                    height={400}
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>

              {/* Celebrate Without Rushing - with Image */}
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="rounded-2xl overflow-hidden shadow-lg">
                  <Image
                    src="/design-images/uploads/1786368611489-vptacm-sc_05.jpg"
                    alt={t('awaits.celebrate.title')}
                    width={500}
                    height={400}
                    className="w-full h-auto object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {t('awaits.celebrate.title')}
                  </h3>
                  <p className="text-lg xl:text-xl text-gray-700 leading-relaxed">
                    {t('awaits.celebrate.body')}
                  </p>
                </div>
              </div>

              {/* Explore the Region - with Image */}
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="order-2 md:order-1">
                  <h3 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {t('awaits.explore.title')}
                  </h3>
                  <p className="text-lg xl:text-xl text-gray-700 leading-relaxed whitespace-pre-line">
                    {t('awaits.explore.body')}
                  </p>
                </div>
                <div className="order-1 md:order-2 rounded-2xl overflow-hidden shadow-lg">
                  <Image
                    src="/design-images/uploads/1786368410690-hbm2wc-dsc02361.jpg"
                    alt={t('awaits.explore.title')}
                    width={500}
                    height={400}
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Journey Section */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-[#faf8f6] order-2 lg:order-1">
          <div className="max-w-lg observe-me slide-right">
            <div className="space-y-6">
              <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
                <span className="text-[#801515]">{t('journey.tagline')}</span>
              </h2>
              <div className="w-16 h-1 bg-[#801515] mb-6" />

              <p className="text-lg xl:text-xl text-gray-600 leading-relaxed">
                {t('journey.p1')}
              </p>
              <p className="text-lg xl:text-xl text-gray-600 leading-relaxed">
                {t('journey.p2')}
              </p>
              <p className="text-lg xl:text-xl text-gray-600 leading-relaxed">
                {t('journey.p3')}
              </p>

              {/* CTA */}
              <div className="pt-4">
                <a
                  href="https://inspiratielafix.com/experienta-act-for-good-magie-intre-ani-la-castle-hotel-daniel/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-[#801515] hover:bg-[#601010] text-white font-serif text-sm sm:text-base rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {t('guestStory')}
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-1 lg:order-2 observe-me slide-left">
          <Image
            src="/design-images/uploads/1786376096418-pa16j7-71bf6ccf-06ac-4ea9-8e31-72f2461e8369-1-1-.jpg"
            alt={t('journey.tagline')}
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
          />
        </div>
      </section>

      {/* Closing Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center py-32 px-6 bg-gradient-to-b from-[#faf8f6] to-white">
        <div className="max-w-4xl mx-auto text-center observe-me slide-up">
          <h2 className="font-poppins text-lg sm:text-3xl md:text-4xl xl:text-5xl font-bold mb-6 leading-[1.1]">
            <span className="text-[#801515]">{t('closing.heading')}</span>
          </h2>
          <p className="text-2xl md:text-3xl text-gray-700 leading-relaxed mb-8">
            {t('closing.body')}
          </p>

          {/* CTA */}
          <Link
            href="/rooms?checkIn=2026-12-30&checkOut=2027-01-03#rooms-list"
            className="group px-[26px] py-6 bg-[#801515] hover:bg-[#601010] text-white font-serif text-xl rounded-full transition-all duration-500 inline-flex items-center justify-center gap-3 shadow-xl hover:shadow-2xl hover:shadow-[#801515]/40 transform hover:scale-105"
          >
            {t('individualOffer.cta')}
          </Link>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 left-20 w-32 h-32 bg-[#801515]/5 rounded-full blur-3xl" style={{ animation: 'breatheSlow 8s ease-in-out infinite' }} />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-[#4A6741]/5 rounded-full blur-3xl" style={{ animation: 'breatheSlow 10s ease-in-out infinite', animationDelay: '2s' }} />
      </section>

      {/* FAQ Section */}
      <FaqSection />

      <Footer />

      {/* Individual Inquiry Modal */}
      <IndividualInquiryModal open={individualInquiryOpen} onClose={() => setIndividualInquiryOpen(false)} />
    </main>
  );
}
